import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Card } from '@/components/Card';
import { CardHeader } from '@/components/CardHeader';
import { StatusDot, type DotStatus } from '@/components/StatusDot';
import { RatioBar } from '@/components/RatioBar';
import { EmptyState } from '@/components/EmptyState';
import { AlertCircle, Plus } from 'lucide-react';
import { me } from '@/lib/auth';
import { formatNumber, formatDate } from '@/lib/locale';
import { apiGet } from '@/features/_shared/queries';
import { QueryState } from '@/features/_shared/QueryState';
import { TenantCreateModal } from './TenantCreateModal';

type Tenant = {
  id: string;
  slug: string;
  name: string;
  status?: 'active' | 'suspended' | 'archived';
  domains_count?: number;
  pass_7d?: number;
  last_report_at?: string;
  alerts_count?: number;
};

type SortKey = 'name' | 'domains_count' | 'pass_7d' | 'alerts_count';
type SortDir = 'asc' | 'desc';

export function TenantList() {
  const tenantsQ = useQuery({
    queryKey: ['tenants'],
    queryFn: () => apiGet<Tenant[]>('/tenants', { tenant: 'all' }),
  });
  const meQ = useQuery({ queryKey: ['auth', 'me'], queryFn: () => me() });
  const canCreate = meQ.data?.platform_role === 'admin' || meQ.data?.platform_role === 'operator';

  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [createOpen, setCreateOpen] = useState(false);

  const sorted = useMemo(() => {
    const list = tenantsQ.data ?? [];
    return [...list].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), 'nl');
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }, [tenantsQ.data, sortKey, sortDir]);

  const onSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  return (
    <div className="max-w-[1400px] space-y-5 p-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight text-slate-900">Tenants</h1>
          <p className="mt-0.5 text-[13px] text-slate-500">
            {tenantsQ.data?.length ?? 0} actieve klanten · klik op een rij voor detail
          </p>
        </div>
        {canCreate && (
          <button
            className="inline-flex items-center gap-1.5 rounded bg-brand-600 px-3 py-1.5 text-[12px] font-medium text-slate-900 hover:bg-brand-500"
            type="button"
            onClick={() => setCreateOpen(true)}
          >
            <Plus size={12} />
            Tenant toevoegen
          </button>
        )}
      </div>

      <TenantCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />

      <QueryState
        isLoading={tenantsQ.isLoading}
        isError={tenantsQ.isError}
        onRetry={() => tenantsQ.refetch()}
      >
        {sorted.length === 0 ? (
          <Card>
            <EmptyState
              title="Nog geen tenants geconfigureerd"
              body="Voeg een eerste tenant toe om DMARC-rapportages te ontvangen. SerSo voorziet de DNS-alias- en report-auth-records binnen één klik."
            />
          </Card>
        ) : (
          <Card>
            <CardHeader title="Tenant overzicht" subtitle={`${sorted.length} klanten`} />
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <SortableTh
                      label="Tenant"
                      sortKey="name"
                      activeKey={sortKey}
                      dir={sortDir}
                      onSort={onSort}
                    />
                    <SortableTh
                      label="# Dom."
                      sortKey="domains_count"
                      activeKey={sortKey}
                      dir={sortDir}
                      onSort={onSort}
                      align="right"
                    />
                    <SortableTh
                      label="Pass 7d"
                      sortKey="pass_7d"
                      activeKey={sortKey}
                      dir={sortDir}
                      onSort={onSort}
                    />
                    <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Laatste rapport
                    </th>
                    <SortableTh
                      label="Alerts"
                      sortKey="alerts_count"
                      activeKey={sortKey}
                      dir={sortDir}
                      onSort={onSort}
                      align="right"
                    />
                    <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sorted.map((t) => (
                    <tr key={t.id} className="group cursor-pointer hover:bg-slate-50">
                      <td className="px-3 py-2.5">
                        <Link
                          to="/tenants/$slug"
                          params={{ slug: t.slug }}
                          className="flex items-center gap-2"
                        >
                          <StatusDot status={dotForStatus(t.status)} />
                          <span className="font-medium text-slate-900 group-hover:text-brand-700">
                            {t.name}
                          </span>
                          <span className="ml-1 font-mono text-[10px] text-slate-400">
                            {t.slug}
                          </span>
                        </Link>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">
                        {formatNumber(t.domains_count ?? 0)}
                      </td>
                      <td className="px-3 py-2.5">
                        <RatioBar pct={t.pass_7d ?? null} />
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">
                        {t.last_report_at ? formatDate(t.last_report_at) : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {(t.alerts_count ?? 0) > 0 ? (
                          <span className="inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-red-700">
                            <AlertCircle size={10} />
                            {t.alerts_count}
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-[11px] text-slate-600">
                          {labelForStatus(t.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </QueryState>
    </div>
  );
}

// Tenant lifecycle status (active | suspended | archived) → coloured dot.
// Falls back to a neutral 'info' dot for any unexpected/empty value.
const STATUS_DOT: Record<string, DotStatus> = {
  active: 'good',
  suspended: 'warn',
  archived: 'onboarding',
};

function dotForStatus(status?: string): DotStatus {
  return STATUS_DOT[status ?? ''] ?? 'info';
}

function labelForStatus(status?: string) {
  const labels: Record<string, string> = {
    active: 'Actief',
    suspended: 'Opgeschort',
    archived: 'Gearchiveerd',
  };
  return labels[status ?? ''] ?? status ?? '—';
}

function SortableTh({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: SortKey;
  activeKey: SortKey;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  align?: 'left' | 'right' | 'center';
}) {
  const active = sortKey === activeKey;
  return (
    <th
      className={`cursor-pointer select-none px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700 text-${align}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && <span className="text-[9px]">{dir === 'desc' ? '↓' : '↑'}</span>}
      </span>
    </th>
  );
}
